import { useCallback } from 'react';

/**
 * Options for configuring image protection behavior
 */
export interface ImageProtectionOptions {
  /** Whether to show a message when protection is triggered */
  showMessage?: boolean;
  /** Custom message to display (defaults to "Image downloading is disabled") */
  customMessage?: string;
  /** Callback invoked when a user attempts to access the context menu or drag the image */
  onAttempt?: () => void;
}

/**
 * Event handlers and styles returned by the useImageProtection hook
 */
export interface ImageProtectionHandlers {
  /** Handler to prevent context menu on right-click */
  onContextMenu: (e: React.MouseEvent) => void;
  /** Handler to prevent drag-and-drop */
  onDragStart: (e: React.DragEvent) => void;
  /** CSS styles to prevent selection and maintain pointer events */
  style: React.CSSProperties;
}

/**
 * Custom hook that provides image protection functionality
 * 
 * Prevents right-click context menu and drag-and-drop on images while
 * maintaining accessibility and normal user interactions.
 * 
 * @param options - Configuration options for protection behavior
 * @returns Event handlers and styles to apply to image elements
 * 
 * @example
 * ```tsx
 * function MyImage() {
 *   const protection = useImageProtection({ 
 *     onAttempt: () => console.log('Protection triggered') 
 *   });
 *   
 *   return <img src="/photo.jpg" alt="Protected" {...protection} />;
 * }
 * ```
 */
export function useImageProtection(
  options?: ImageProtectionOptions
): ImageProtectionHandlers {
  const { showMessage = false, customMessage = 'Image downloading is disabled', onAttempt } = options || {};

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      try {
        e.preventDefault();
        e.stopPropagation();
        
        if (onAttempt) {
          onAttempt();
        }
        
        if (showMessage) {
          // Simple alert for now - can be replaced with toast notification
          alert(customMessage);
        }
      } catch (error) {
        console.error('Error in image protection context menu handler:', error);
      }
    },
    [onAttempt, showMessage, customMessage]
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      try {
        e.preventDefault();
        e.stopPropagation();
        
        if (onAttempt) {
          onAttempt();
        }
      } catch (error) {
        console.error('Error in image protection drag handler:', error);
      }
    },
    [onAttempt]
  );

  const protectionStyle: React.CSSProperties = {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    pointerEvents: 'auto',
    WebkitTouchCallout: 'none', // iOS Safari
  };

  return {
    onContextMenu: handleContextMenu,
    onDragStart: handleDragStart,
    style: protectionStyle,
  };
}
